import axios from './axiosConfig'
import { generateClientUuid } from 'utils/clientUuid'

export type PendingTakeawayDraftItem = {
  product_id: number
  quantity: number
  unit_id?: number | null
  unit_price: number
  discount?: number
  variant_id?: string | null
  selected_options?: Array<{ product_option_group_id: number; option_ids: number[] }>
  client_cart_line_id?: string | null
  kitchen_route_type?: 'screen' | 'printer' | 'none' | null
  kitchen_station_id?: string | null
  kitchen_printer_id?: string | null
  kitchen_printed_quantity?: number
  kitchen_printed_at?: string | null
  notes?: string | null
}

export type PendingTakeawayDraftPayload = {
  client_uuid?: string
  client_op_id?: string | null
  expected_sale_updated_at?: string | null
  expected_cart_revision?: number | null
  price_defining_options_version?: number
  items: PendingTakeawayDraftItem[]
  subtotal?: number
  tax?: number
  discount?: number
  promotion_discount?: number
  coupon_id?: string | null
  coupon_discount?: number
  service_charge?: number
  total?: number
  customer_id?: number | null
  notes?: string | null
}

export type PendingKitchenDispatchResult = {
  sale_id?: number
  mode?: 'local' | 'server_queue' | string
  action?: string
  jobs_created?: number
  watermark_advanced?: boolean
  physical_print_claimed?: boolean
  unprinted_quantity?: number
  cart_revision?: number
  code?: string
}

export type PendingTakeawaySale = Record<string, unknown> & {
  id?: number
  client_uuid?: string
  invoice_number?: string | null
  print_sequence?: number | string | null
  status?: string | null
  paid?: number | string | null
  total?: number | string | null
  notes?: string | null
  created_at?: string | null
  updated_at?: string | null
  cart_revision?: number | null
  kitchen_status?: string | null
  kitchen_sent_at?: string | null
  items?: unknown[]
  branch_id?: string | null
}

export type PendingTakeawayOfflineSyncOp = {
  seq: number
  client_op_id: string
  action: 'create' | 'update' | 'kitchen_ack' | 'settle' | 'cancel'
  payload: Record<string, unknown>
}

export type PendingTakeawayOfflineSyncPayload = {
  client_uuid: string
  branch_id?: string | null
  shift_id?: string | null
  pos_register_id?: string | null
  register_session_id?: string | null
  operations: PendingTakeawayOfflineSyncOp[]
}

export type PendingTakeawayOfflineSyncOpResult = {
  seq?: number
  client_op_id?: string | null
  action?: string
  status?: 'synced' | 'conflict' | 'failed' | 'skipped' | string
  code?: string
  message?: string
  recoverable?: boolean
  allowed_actions?: string[]
  order_client_uuid?: string | null
  payment_line_client_id?: string | null
  field?: string | null
  details?: Record<string, unknown>
  sale_id?: number
  cart_revision?: number
  result?: Record<string, unknown> | null
}

export type PendingTakeawayOfflineSyncResult = {
  status?: string
  code?: string
  message?: string
  client_uuid?: string
  recoverable?: boolean
  allowed_actions?: string[]
  order_client_uuid?: string | null
  payment_line_client_id?: string | null
  field?: string | null
  details?: Record<string, unknown>
  sale_id?: number | null
  operations?: PendingTakeawayOfflineSyncOpResult[]
  data?: PendingTakeawaySale
}

export type PendingTakeawayApiError = {
  http?: number
  code?: string
  message?: string
  sale?: PendingTakeawaySale | null
}

export function parsePendingTakeawayError(error: unknown): PendingTakeawayApiError {
  const response = (error as { response?: { status?: number; data?: Record<string, unknown> } })?.response
  const data = response?.data
  const sale = data?.data && typeof data.data === 'object' ? (data.data as PendingTakeawaySale) : null
  return {
    http: response?.status,
    code: typeof data?.code === 'string' ? data.code : undefined,
    message: typeof data?.message === 'string' ? data.message : undefined,
    sale,
  }
}

function unwrapSale(payload: { status?: string; data?: PendingTakeawaySale } | PendingTakeawaySale): PendingTakeawaySale {
  if (payload && typeof payload === 'object' && 'data' in payload && payload.data && typeof payload.data === 'object') {
    return payload.data as PendingTakeawaySale
  }
  return payload as PendingTakeawaySale
}

export const pendingTakeawayAPI = {
  list: async (): Promise<PendingTakeawaySale[]> => {
    const response = await axios.get<{ status: string; data: PendingTakeawaySale[] }>('/pos/takeaway/pending')
    return Array.isArray(response.data?.data) ? response.data.data : []
  },

  get: async (id: number): Promise<PendingTakeawaySale> => {
    const response = await axios.get<{ status: string; data: PendingTakeawaySale }>(`/pos/takeaway/pending/${id}`)
    return unwrapSale(response.data)
  },

  create: async (payload: PendingTakeawayDraftPayload): Promise<PendingTakeawaySale> => {
    const response = await axios.post<{ status: string; data: PendingTakeawaySale }>(
      '/pos/takeaway/pending',
      payload,
    )
    return unwrapSale(response.data)
  },

  update: async (id: number, payload: PendingTakeawayDraftPayload): Promise<PendingTakeawaySale> => {
    const response = await axios.put<{ status: string; data: PendingTakeawaySale }>(
      `/pos/takeaway/pending/${id}`,
      payload,
    )
    return unwrapSale(response.data)
  },

  dispatchKitchen: async (
    id: number,
    payload: { client_op_id?: string | null } = {},
  ): Promise<PendingKitchenDispatchResult> => {
    const response = await axios.post<{ status: string; data: PendingKitchenDispatchResult }>(
      `/pos/takeaway/pending/${id}/kitchen`,
      payload,
    )
    return response.data?.data ?? {}
  },

  reprintKitchen: async (
    id: number,
    payload: { client_op_id?: string | null; sale_item_ids?: number[] } = {},
  ): Promise<PendingKitchenDispatchResult> => {
    const response = await axios.post<{ status: string; data: PendingKitchenDispatchResult }>(
      `/pos/takeaway/pending/${id}/kitchen/reprint`,
      payload,
    )
    return response.data?.data ?? {}
  },

  settle: async (id: number, payload: Record<string, unknown>): Promise<PendingTakeawaySale> => {
    const response = await axios.post<{ status: string; data: PendingTakeawaySale }>(
      `/pos/takeaway/pending/${id}/settle`,
      payload,
    )
    return unwrapSale(response.data)
  },

  cancel: async (
    id: number,
    payload: { client_op_id?: string | null; expected_cart_revision?: number | null } = {},
  ): Promise<PendingTakeawaySale> => {
    const response = await axios.post<{ status: string; data: PendingTakeawaySale }>(
      `/pos/takeaway/pending/${id}/cancel`,
      payload,
    )
    return unwrapSale(response.data)
  },

  offlineSync: async (payload: PendingTakeawayOfflineSyncPayload): Promise<PendingTakeawayOfflineSyncResult> => {
    const response = await axios.post<PendingTakeawayOfflineSyncResult>(
      '/pos/takeaway/pending/offline-sync',
      payload,
    )
    return response.data
  },
}

export function nextPendingClientOpId(): string {
  return generateClientUuid()
}
